import { mapState } from 'vuex';
import { validImageFileName, readableFileSizeFromBytes as readableFileSizeUtil, randomInt } from '@/utils/vuex';
export default {
  name: 'CoreDtwinAttachments',
  props: {
    accept: {
      type: String,
      default: '*/*'
    },
    source: {
      type: String,
      default: 'digitaltwin'
    },
    selectableImages: {
      type: Boolean,
      default: true
    },
    selectedAttachments: {
      type: Array,
      default: () => []
    },
    attachments: {
      type: Array,
      default: () => []
    },
    disabled: {
      type: Boolean,
      default: false,
      required: false
    },
    apiVersion: {
      type: Number,
      default: 1,
      required: false
    }
  },
  data: function () {
    return {
      attachmentsInternal: [],
      showPreviewDialog: false,
      imagePreviewUrl: '',
      errorMessage: '',
      uploads: [],
      queue: []
    };
  },
  computed: {
    ...mapState('app', ['snackbar', 'inpOutlined', 'inpFilled', 'image', 'color', 'UiTheme']),
    buttons() {
      return [{
        title: this.$i18n_t('DigitalTwin.DigitalTwin.attachmentButtonAdd'),
        icon: 'fal fa-plus',
        action: 'addItem',
        valid: !this.disabled && this.accept !== ''
      }];
    },
    maxNumberAttachments() {
      return 10;
    },
    maxBytePerAttachment() {
      switch (this.source) {
        case 'devicetype':
          return 20000000;
        // 20 Megabyte
        default:
          return 10000000;
        // 10 Megabye
      }
    }
  },
  watch: {
    attachments() {
      this.attachmentsInternal = this.attachments || [];
    }
  },
  mounted() {
    this.attachmentsInternal = this.attachments || [];
  },
  methods: {
    // isItemSelectable(item) {
    //   // possibility to select image icons for twins
    //   return this.selectableImages && this.source === 'digitaltwin' && validImageFileName(item.name);
    // },
    isImage(item) {
      return this.selectableImages && this.source === 'digitaltwin' && validImageFileName(item.name);
    },
    itemSelectionStatus(item) {
      return this.selectedAttachments.indexOf(item.id) >= 0;
    },
    itemSelected(item) {
      if (this.source === 'digitaltwin') {
        if (validImageFileName(item.name)) {
          this.$emit('image-selected', item);
        }
      }
      this.$emit('item-selected', item);
    },
    itemDeselected(item) {
      // this is also called if a item is deleted, so make sure only to emit actually selected deleted items
      if (this.itemSelectionStatus(item)) {
        if (this.source === 'digitaltwin') {
          if (validImageFileName(item.name)) {
            this.$emit('image-deselected', item);
          }
        }
        this.$emit('item-deselected', item);
      }
    },
    imagePreviewLoaded() {
      // @TODO: implement visual loading cue and hide it and show loaded image here
    },
    addItem() {
      this.$refs.attachmentInput.click();
    },
    readableFileSizeFromBytes: readableFileSizeUtil,
    fileInputClicked() {
      this.errorMessage = '';
    },
    fileInputChanged() {
      const files = this.$refs.attachmentInput.files;
      if (!this.validateFileNumberLimit(files) || !this.validateFileSizeLimit(files)) {
        this.$refs.attachmentInput.value = '';
        return;
      }

      // if there is a listener set, call that one and let it handle/manipulate the files before uploading
      if (this.$listeners && this.$listeners['files:custom-selection']) {
        this.$emit('files:custom-selection', Array.from(this.$refs.attachmentInput.files));
      } else {
        this.queue = Array.from(this.$refs.attachmentInput.files);
        this.addFilesToUploads();
      }
      this.$refs.attachmentInput.value = '';
    },
    validateFileNumberLimit(files) {
      if (files && Array.from(files).length + this.attachmentsInternal.length > this.maxNumberAttachments) {
        this.errorMessage = this.$i18n_t('DigitalTwin.DigitalTwin.attachmentErrorLimitExceeded', {
          attachtmentMaxNumber: this.maxNumberAttachments
        });
        return false;
      }
      return true;
    },
    validateFileSizeLimit(files) {
      if (files) {
        const rejectedFile = Array.from(files).filter(file => file.size >= this.maxBytePerAttachment);
        if (rejectedFile.length > 0) {
          this.errorMessage = this.$i18n_t('DigitalTwin.DigitalTwin.attachmentErrorFileTooBig', {
            attachtmentMaxSize: this.readableSizeFromBytes(this.maxBytePerAttachment)
          });
          return false;
        }
      }
      return true;
    },
    addFilesToUploads() {
      const file = this.queue.shift();
      if (file) {
        this.uploads.push({
          id: `${Date.now()}${randomInt(2000)}`,
          name: file.name,
          size: file.size,
          file: file
        });
      }
    },
    previewHandler(fileUrl) {
      this.imagePreviewUrl = fileUrl;
      this.showPreviewDialog = true;
    },
    updateHandler(attachment) {
      if (attachment) {
        this.uploads = this.uploads.filter(upload => upload.id !== attachment.item.id);
        let attachmentItem = {};
        if (this.source === 'devicetype') {
          attachmentItem = {
            name: attachment.item.name,
            size: attachment.item.size,
            storagePath: attachment.fileId,
            type: attachment.item.file.type
          };
        } else {
          attachmentItem = {
            id: attachment.fileId,
            name: attachment.item.name,
            size: attachment.item.size
          };
        }
        this.attachmentsInternal = this.attachmentsInternal.concat(attachmentItem);
        this.$emit('update', this.attachmentsInternal);
      }
      this.addFilesToUploads();
    },
    removeHandler(file) {
      this.errorMessage = '';
      let removedItem = {};
      if (this.source === 'devicetype') {
        removedItem = this.attachmentsInternal.find(item => item.storagePath !== file.storagePath);
        this.attachmentsInternal = this.attachmentsInternal.filter(item => item.storagePath !== file.storagePath);
      } else {
        removedItem = this.attachmentsInternal.find(item => item.id !== file.id);
        this.attachmentsInternal = this.attachmentsInternal.filter(item => item.id !== file.id);
      }
      this.itemDeselected(removedItem || {});
      this.$emit('update', this.attachmentsInternal);
    },
    readableSizeFromBytes(bytes) {
      if (typeof bytes !== 'number' || isNaN(bytes)) {
        return '';
      }
      let exponent;
      let unit;
      const neg = bytes < 0;
      const units = ['B', 'kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      if (neg) {
        bytes = -bytes;
      }
      if (bytes < 1) {
        return `${(neg ? '-' : '') + bytes} B`;
      }
      exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1000)), units.length - 1);
      bytes = (bytes / Math.pow(1000, exponent)).toFixed(2);
      unit = units[exponent];
      return `${(neg ? '-' : '') + bytes} ${unit}`;
    }
  }
};